// @flow

import React, { useState } from 'react';

import * as Import from '@ta/platform.import';
import AdTarget from '@ta/cpm.ad-target-for-modal';
import {
  listenToCpmMessage,
  registerRenderEventListener,
  isEmptyOrCollapser,
} from '@ta/cpm.adsupport';
import Radio from '@ta/common.radio';
import { featureIsEnabled } from '@ta/common.features';

export type DmoCompShelfSponsorData = {|
  /** DMO user ID */
  dmoUserID: number,

  /** Location id */
  pinGeoID: number,

  /** A list of impression trackers */
  impressionTrackers?: Array<string>,

  /** URL provided by DFP for tracking (redirecting when user clicks) */
  clickTracker?: string,

  /** Creative ID of the ad */
  creativeId?: string,
|};

/** provider function for dmo competitor sponsor data */
export type DmoCompShelfSponsorDataProvider = ((DmoCompShelfSponsorData) => void) => void;

type ValidPosition = 'feeddiscover';

type Props = {|
  /** Identifier for ad position */
  adPosition: ValidPosition,
  locationId: number,
  clusterId: string,
  collapserFunction: () => void,
|};

// only import the next component if the ad injects
const DmoCompetitor = Import.component.onUsage(() => import('./DmoCompetitor'));

const DmoCompShelfSponsor = ({ adPosition, locationId, clusterId, collapserFunction }: Props) => {
  const [sponsorData, setSponsorData] = useState({
    dmoUserID: '',
    pinGeoID: 0,
    impressionTrackers: [],
    clickTracker: '',
    creativeId: '',
    adCallReturned: false,
  });
  const [dfpRequested, setDfpRequested] = useState(0);
  const adTargetRadio = Radio(`dmocompshelf-sponsor-${adPosition}`);

  // May need to revise the feature.
  const isDmoCompShelfEnabled = () => featureIsEnabled('allow_dmo_dfp_ad_shelf');

  if (isDmoCompShelfEnabled()) {
    // the cpm message string we need to listen to is determined by the
    // the dfp ad's html. This must be coordinated manually with whoever sets up
    // the ad for each ad type
    listenToCpmMessage('dmoCompShelf', data => {
      if (sponsorData.adCallReturned) {
        console.error(
          'tried to inject dmoCompShelf ad a second time after the ad call already returned once',
        );
      }
      const { dmoUserID, pinGeoID, impressionTrackers, clickTracker, creativeId } = data;
      setSponsorData({
        dmoUserID,
        pinGeoID,
        impressionTrackers,
        clickTracker,
        creativeId,
        adCallReturned: true,
      });
    });
    registerRenderEventListener('gpt-ad-3x1-feeddiscover', event => {
      if (isEmptyOrCollapser(event) && !sponsorData.adCallReturned) {
        collapserFunction();
        setSponsorData({
          dmoUserID: '',
          pinGeoID: 0,
          impressionTrackers: [],
          clickTracker: '',
          creativeId: '',
          adCallReturned: true,
        });
      }
    });
  }

  // need to wait until the component finishes rendering the first time before injecting the ad
  React.useEffect(() => {
    if (!dfpRequested) {
      setDfpRequested(Date.now());
      adTargetRadio.emit('refreshAds');
    }
  }, [dfpRequested, sponsorData.pinGeoID, adTargetRadio]);

  const adCallDataIsValid = sponsorData.dmoUserID.length > 0 && sponsorData.pinGeoID > 0;
  const adCallSucceeded = sponsorData.adCallReturned && adCallDataIsValid;
  return isDmoCompShelfEnabled() ? (
    <React.Fragment>
      <AdTarget size="3x1" position={adPosition} defer hidden radio={adTargetRadio} />
      {adCallSucceeded && (
        <DmoCompetitor
          competitorGeo={sponsorData.pinGeoID}
          competitorUserId={sponsorData.dmoUserID}
          currentGeo={locationId}
          clickTracker={sponsorData.clickTracker}
          impressionTrackers={sponsorData.impressionTrackers}
          clusterId={clusterId}
        />
      )}
    </React.Fragment>
  ) : null;
};

export default DmoCompShelfSponsor;
